import { Head, Link } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import PageHeader from '@/components/ui/PageHeader'
import { ViewColumnsIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { useState, useEffect, useCallback, useRef } from 'react'
import { TableCellsIcon, Bars3BottomLeftIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/20/solid'
import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
  DropdownDivider,
  DropdownLabel,
} from '@/components/catalyst/dropdown'
import { Checkbox } from '@/components/catalyst/checkbox'

// ─── Types ───────────────────────────────────────────────────

interface Order {
  id: number
  name: string
  state_updated_at: string
  tot_selling: number
}

interface Task {
  id: number
  title: string
  customer_order_id: number
}

interface PipelineProps {
  states: Record<string, number>
  ordersByState: Record<string, Order[]>
  tasks: Task[]
  taskTitles: { id: number; content: string }[]
  users: { id: number; name: string }[]
  orders: { id: number; po_number: string }[]
}

// ─── Constants ───────────────────────────────────────────────

const COLUMN_ORDER = [
  'new_deal', 'deals_to_review', 'deals_to_place', 'deals_requiring_deposit',
  'unconfirmed_vendor_po', 'confirmed_orders', 'vendor_payments', 'needs_routing',
  'shipped_from_brand', 'in_warehouse', 'scanned', 'partially_scanned',
  'ready_to_ship', 'released', 'shipped_to_client', 'vendor_po_issues',
  'consignment', 'stock',
]

const STATE_TITLES: Record<string, string> = {
  new_deal: 'New Deal', deals_to_review: 'Deals To Review', deals_to_place: 'Deals To Place',
  deals_requiring_deposit: 'Deals Requiring Deposit', unconfirmed_vendor_po: 'Unconfirmed Vendor PO',
  confirmed_orders: 'Confirmed Orders', vendor_payments: 'Vendor Payments',
  needs_routing: 'Needs Routing', shipped_from_brand: 'Shipped From Brand',
  in_warehouse: 'In Warehouse', scanned: 'Scanned', partially_scanned: 'Partially Scanned',
  ready_to_ship: 'Ready To Ship', released: 'Released', shipped_to_client: 'Shipped To Client',
  vendor_po_issues: 'Vendor PO Issues', consignment: 'Consignment', stock: 'Stock',
}

const VIEW_KEY = 'pipeline-view'
const HIDDEN_COLS_KEY = 'pipeline-hidden-cols'
const COL_WIDTHS_KEY = 'pipeline-col-widths'
const DEFAULT_COL_WIDTH = 220
const MIN_COL_WIDTH = 150
const MAX_COL_WIDTH = 500

// ─── Helpers ─────────────────────────────────────────────────

function daysSince(dateStr: string): number {
  return Math.floor(Math.abs(Date.now() - new Date(dateStr).getTime()) / 86400000)
}

function ageDot(stateUpdatedAt: string): string {
  const d = daysSince(stateUpdatedAt)
  if (d > 30) return 'bg-red-500'
  if (d >= 8) return 'bg-orange-500'
  if (d >= 4) return 'bg-emerald-500'
  return 'bg-sky-500'
}

function fmt(n: number): string {
  return parseFloat(n.toFixed(2)).toLocaleString('en-US') + ' $'
}

// ─── Order cell (shared) ─────────────────────────────────────

function OrderRow({ order, taskCount }: { order: Order; taskCount: number }) {
  return (
    <tr className="group hover:bg-zinc-50 dark:hover:bg-zinc-800/50">
      <td className="w-7 py-1 pl-2 pr-2.5">
        <span className={clsx('inline-block h-2 w-2 rounded-full', ageDot(order.state_updated_at))} />
      </td>
      <td className="max-w-0 overflow-hidden py-1 pr-1">
        <Link href={`/inertia_customer_orders/${order.id}`} className="block truncate text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400" title={`#${order.id} ${order.name}`}>
          #{order.id} {order.name}
        </Link>
      </td>
      <td className="w-20 whitespace-nowrap py-1 pr-1 text-right text-xs text-zinc-500">{fmt(order.tot_selling)}</td>
      <td className="w-6 whitespace-nowrap py-1 pr-1 text-right text-xs font-bold text-indigo-600 dark:text-indigo-400">
        <Link href={`/tasks/filtered_by_order_id/${order.id}`} className="hover:underline">{taskCount}</Link>
      </td>
    </tr>
  )
}

// ─── Column resize hook ──────────────────────────────────────

function useColumnWidths(_columns: string[]) {
  const [widths, setWidths] = useState<Record<string, number>>(() => {
    if (typeof window === 'undefined') return {}
    try {
      const saved = JSON.parse(localStorage.getItem(COL_WIDTHS_KEY) || '{}')
      return saved
    } catch { return {} }
  })

  useEffect(() => {
    localStorage.setItem(COL_WIDTHS_KEY, JSON.stringify(widths))
  }, [widths])

  const getWidth = (col: string) => widths[col] || DEFAULT_COL_WIDTH

  const [resizingCol, setResizingCol] = useState<string | null>(null)
  const startX = useRef(0)
  const startW = useRef(0)

  const startResize = useCallback((col: string, e: React.MouseEvent) => {
    e.preventDefault()
    setResizingCol(col)
    startX.current = e.clientX
    startW.current = widths[col] || DEFAULT_COL_WIDTH
  }, [widths])

  useEffect(() => {
    if (!resizingCol) return
    const onMove = (e: MouseEvent) => {
      const delta = e.clientX - startX.current
      const newW = Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, startW.current + delta))
      setWidths(prev => ({ ...prev, [resizingCol]: newW }))
    }
    const onUp = () => setResizingCol(null)
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [resizingCol])

  return { getWidth, startResize, isResizing: !!resizingCol }
}

// ─── Table view (columns) ────────────────────────────────────

function TableView({ columns, ordersByState, tasks }: {
  columns: string[]; ordersByState: Record<string, Order[]>; tasks: Task[]
}) {
  const { getWidth, startResize, isResizing } = useColumnWidths(columns)

  return (
    <div className="overflow-x-auto" style={{ cursor: isResizing ? 'col-resize' : undefined }}>
      <div className="inline-flex min-w-full">
        {columns.map((state) => {
          const orders = ordersByState[state] || []
          const total = orders.reduce((s, o) => s + o.tot_selling, 0)
          const w = getWidth(state)

          return (
            <div key={state} className="relative shrink-0 border-r border-zinc-200 last:border-r-0 dark:border-zinc-700" style={{ width: w }}>
              <table className="w-full table-fixed text-left text-xs">
                <colgroup>
                  <col style={{ width: 28 }} />
                  <col />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 24 }} />
                </colgroup>
                <thead className="sticky top-0 z-10">
                  {/* Totals row */}
                  <tr className="border-b border-zinc-200 bg-zinc-50/50 text-[10px] font-semibold text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800/30 dark:text-zinc-400">
                    <th colSpan={4} className="bg-zinc-50/50 py-1 pl-2 pr-2 dark:bg-zinc-800/30">
                      {fmt(total)} <span className="font-normal text-zinc-400">({orders.length})</span>
                    </th>
                  </tr>
                  {/* Column header */}
                  <tr className="border-b border-zinc-200 bg-white text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400">
                    <th className="py-1 pl-2"></th>
                    <th className="overflow-hidden py-1 pr-1 truncate">{STATE_TITLES[state] || state}</th>
                    <th className="py-1 pr-1 text-right">$</th>
                    <th className="py-1 pr-1 text-right">T</th>
                  </tr>
                </thead>
              </table>
              <div className="max-h-[calc(100vh-220px)] overflow-y-auto">
                <table className="w-full table-fixed text-left text-xs">
                  <colgroup>
                    <col style={{ width: 28 }} />
                    <col />
                    <col style={{ width: 80 }} />
                    <col style={{ width: 24 }} />
                  </colgroup>
                  <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                    {orders.map((order) => (
                      <OrderRow key={order.id} order={order} taskCount={tasks.filter(t => t.customer_order_id === order.id).length} />
                    ))}
                  </tbody>
                </table>
                {orders.length === 0 && <div className="py-3 text-center text-[10px] text-zinc-300">—</div>}
              </div>

              {/* Resize handle */}
              <div
                onMouseDown={(e) => startResize(state, e)}
                className="absolute inset-y-0 right-0 z-20 w-1 cursor-col-resize hover:bg-indigo-500/30 active:bg-indigo-500/50"
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Rows view (stacked) ─────────────────────────────────────

function RowsView({ columns, ordersByState, tasks }: {
  columns: string[]; ordersByState: Record<string, Order[]>; tasks: Task[]
}) {
  return (
    <div className="space-y-4">
      {columns.map((state) => {
        const orders = ordersByState[state] || []
        if (orders.length === 0) return null
        const total = orders.reduce((s, o) => s + o.tot_selling, 0)

        return (
          <div key={state}>
            <table className="w-full table-fixed text-left text-xs">
              <colgroup>
                <col style={{ width: 28 }} />
                <col />
                <col style={{ width: 100 }} />
                <col style={{ width: 32 }} />
              </colgroup>
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50/50 text-[10px] font-semibold text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800/30 dark:text-zinc-400">
                  <th colSpan={4} className="py-1 pl-2 pr-2">{fmt(total)} <span className="font-normal text-zinc-400">({orders.length})</span></th>
                </tr>
                <tr className="border-b border-zinc-200 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:border-zinc-700 dark:text-zinc-400">
                  <th className="py-1 pl-2"></th>
                  <th className="py-1 pr-1">{STATE_TITLES[state] || state}</th>
                  <th className="py-1 pr-1 text-right">$</th>
                  <th className="py-1 pr-1 text-right">T</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                {orders.map((order) => (
                  <OrderRow key={order.id} order={order} taskCount={tasks.filter(t => t.customer_order_id === order.id).length} />
                ))}
              </tbody>
            </table>
          </div>
        )
      })}
    </div>
  )
}

// ─── Legend ───────────────────────────────────────────────────

function MemoLegend() {
  return (
    <details className="mb-3 rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
      <summary className="cursor-pointer text-xs font-medium text-zinc-500">
        Color legend — days since last state change
      </summary>
      <div className="mt-2 flex gap-3 text-xs font-medium">
        <span className="flex items-center gap-1.5 rounded-md bg-sky-50 px-2 py-1 text-zinc-700 dark:bg-sky-500/10 dark:text-zinc-300"><span className="h-1.5 w-1.5 rounded-full bg-sky-500" />0-3 days</span>
        <span className="flex items-center gap-1.5 rounded-md bg-emerald-50 px-2 py-1 text-zinc-700 dark:bg-emerald-500/10 dark:text-zinc-300"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />4-7 days</span>
        <span className="flex items-center gap-1.5 rounded-md bg-orange-50 px-2 py-1 text-zinc-700 dark:bg-orange-500/10 dark:text-zinc-300"><span className="h-1.5 w-1.5 rounded-full bg-orange-500" />8-30 days</span>
        <span className="flex items-center gap-1.5 rounded-md bg-red-50 px-2 py-1 text-zinc-700 dark:bg-red-500/10 dark:text-zinc-300"><span className="h-1.5 w-1.5 rounded-full bg-red-500" />&gt;30 days</span>
      </div>
    </details>
  )
}

// ─── Column filter dropdown (Catalyst) ───────────────────────

function ColumnFilter({ hiddenCols, setHiddenCols, allColumns }: {
  hiddenCols: string[]; setHiddenCols: (cols: string[]) => void; allColumns: string[]
}) {
  const toggle = (col: string) => {
    setHiddenCols(
      hiddenCols.includes(col)
        ? hiddenCols.filter(c => c !== col)
        : [...hiddenCols, col]
    )
  }

  return (
    <Dropdown>
      <DropdownButton
        as="button"
        className="flex items-center gap-1 rounded-lg border border-zinc-200 px-2.5 py-1.5 text-xs font-medium text-zinc-500 hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-white/5"
        title="Show/hide columns"
      >
        <AdjustmentsHorizontalIcon className="h-4 w-4" />
        {hiddenCols.length > 0 && (
          <span className="text-[10px] text-indigo-600 dark:text-indigo-400">{hiddenCols.length}</span>
        )}
      </DropdownButton>
      <DropdownMenu anchor="bottom end" className="z-50 max-h-80 w-56 overflow-y-auto">
        {allColumns.map(col => (
          <DropdownItem key={col} onClick={() => toggle(col)}>
            <Checkbox checked={!hiddenCols.includes(col)} className="pointer-events-none" data-slot="icon" />
            <DropdownLabel className={clsx(
              hiddenCols.includes(col) && 'text-zinc-400 line-through dark:text-zinc-500'
            )}>
              {STATE_TITLES[col] || col}
            </DropdownLabel>
          </DropdownItem>
        ))}
        {hiddenCols.length > 0 && (
          <>
            <DropdownDivider />
            <DropdownItem onClick={() => setHiddenCols([])}>
              <DropdownLabel className="text-indigo-600 dark:text-indigo-400">Show all</DropdownLabel>
            </DropdownItem>
          </>
        )}
      </DropdownMenu>
    </Dropdown>
  )
}

// ─── View toggle button ──────────────────────────────────────

function ViewToggle({ view, setView }: { view: 'table' | 'rows'; setView: (v: 'table' | 'rows') => void }) {
  return (
    <div className="flex rounded-lg border border-zinc-200 dark:border-zinc-700">
      <button
        onClick={() => setView('table')}
        className={clsx(
          'flex items-center gap-1 rounded-l-lg px-2.5 py-1.5 text-xs font-medium transition-colors',
          view === 'table'
            ? 'bg-indigo-500 text-white'
            : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400'
        )}
        title="Columns"
      >
        <TableCellsIcon className="h-4 w-4" />
      </button>
      <button
        onClick={() => setView('rows')}
        className={clsx(
          'flex items-center gap-1 rounded-r-lg px-2.5 py-1.5 text-xs font-medium transition-colors',
          view === 'rows'
            ? 'bg-indigo-500 text-white'
            : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400'
        )}
        title="Rows"
      >
        <Bars3BottomLeftIcon className="h-4 w-4" />
      </button>
    </div>
  )
}

// ─── Main ────────────────────────────────────────────────────

export default function PipelineIndex({ ordersByState, tasks }: PipelineProps) {
  const [view, setView] = useState<'table' | 'rows'>(() => {
    if (typeof window === 'undefined') return 'table'
    return (localStorage.getItem(VIEW_KEY) as 'table' | 'rows') || 'table'
  })

  const [hiddenCols, setHiddenCols] = useState<string[]>(() => {
    if (typeof window === 'undefined') return []
    try { return JSON.parse(localStorage.getItem(HIDDEN_COLS_KEY) || '[]') } catch { return [] }
  })

  useEffect(() => { localStorage.setItem(VIEW_KEY, view) }, [view])
  useEffect(() => { localStorage.setItem(HIDDEN_COLS_KEY, JSON.stringify(hiddenCols)) }, [hiddenCols])

  const allColumns = COLUMN_ORDER.filter((s) => ordersByState[s] !== undefined)
  const visibleColumns = allColumns.filter((s) => !hiddenCols.includes(s))

  return (
    <>
      <Head title="Pipeline" />
      <PageHeader
        title="Orders Statuses Pipeline"
        subtitle="Browse Active Orders by Statuses"
        icon={<ViewColumnsIcon className="h-5 w-5" />}
        actions={
          <>
            <ColumnFilter hiddenCols={hiddenCols} setHiddenCols={setHiddenCols} allColumns={allColumns} />
            <ViewToggle view={view} setView={setView} />
          </>
        }
      />
      <MemoLegend />
      {view === 'table'
        ? <TableView columns={visibleColumns} ordersByState={ordersByState} tasks={tasks} />
        : <RowsView columns={visibleColumns} ordersByState={ordersByState} tasks={tasks} />
      }
    </>
  )
}

PipelineIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
